import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAI } from '../../context/AIContext';
import { useEdit } from '../../context/EditContext';
import { AuthContext } from '../../../core/context/AuthContext';
import { ArrowRightIcon, CheckCircleIcon, ClipboardIcon, CloseIcon, GearIcon, SendIcon, TargetIcon } from './icons/AIIcons';
import WebsiteProfileDropdown from './WebsiteProfileDropdown';
import { PrimaryButton } from '../../../design-system';
import { typography, surfaces, borders, textColors } from '../../../design-system/tokens';
import { useNavigate } from 'react-router-dom';
import AIAssetLibraryModal from './AIAssetLibraryModal';
import useChatAssetPins from './hooks/useChatAssetPins';

// NOTE: The AI backend cannot browse/scrape arbitrary URLs. This guard prevents the chat from
// sending "please read this link" requests that would otherwise lead to hallucinations.
const URL_REGEX = /\bhttps?:\/\/[^\s<>()]+|\bwww\.[^\s<>()]+/gi;
const LINK_SCRAPE_INTENT_REGEX =
  /\b(read|scrape|crawl|extract|summari[sz]e|translate|analy[sz]e|review|parse|look\s+at|visit|open|check)\b/i;
const LINK_SOURCE_PHRASE_REGEX = /\bfrom\s+(this|the)\s+(link|url|website|page)\b/i;

const extractUrlsFromText = (text) => {
  const s = String(text || "");
  const matches = s.match(URL_REGEX) || [];
  const uniq = Array.from(new Set(matches.map((m) => String(m).trim()).filter(Boolean)));
  // Keep the list small for UI safety.
  return uniq.slice(0, 5);
};

const detectLinkScrapeRequest = (text) => {
  const urls = extractUrlsFromText(text);
  if (urls.length === 0) return { shouldBlock: false, urls: [] };
  const s = String(text || "");
  const hasIntent = LINK_SCRAPE_INTENT_REGEX.test(s) || LINK_SOURCE_PHRASE_REGEX.test(s);
  return { shouldBlock: hasIntent, urls };
};

const buildLinkScrapeBlockedMessage = (urls = []) => {
  const first = Array.isArray(urls) && urls.length > 0 ? urls[0] : null;
  return [
    `I can’t open, browse, or scrape links from here${first ? ` (like ${first}).` : "."}`,
    "",
    'If you want me to use the content from a webpage, please paste the relevant text here, or upload it as a PDF/DOC (or a screenshot) using the "Attach assets" button (clipboard icon) in this chat.',
    "",
    "If you only want to add the URL as a link/button, tell me where to place it and what label to use.",
  ].join("\n");
};

const AIInputBox = ({
  chatId,
  selectedElement,
  addEphemeralMessage = () => {},
  removeEphemeralMessage = () => {},
  enableEphemeralWarnings = false,
  draftValue,
  onDraftChange,
  textareaRef = null,
  chatType = 'standard',
  onTextareaFocus = () => {},
  onTextareaBlur = () => {},
  isReviewMode = false,
}) => {
  const { sendMessage, sending, currentChat, updatePinnedAssets } = useAI();
  const { pageId, websiteId } = useEdit();
  const authContextValue = useContext(AuthContext) || {};
  // Back-compat: some parts of the app/tests provide { user }, others provide { auth }.
  const username = authContextValue?.auth?.username || authContextValue?.user?.username || null;
  const navigate = useNavigate();
  const [internalInput, setInternalInput] = useState('');
  const [quotaWarning, setQuotaWarning] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [preflightWarning, setPreflightWarning] = useState(null);
  const [assetMenuOpen, setAssetMenuOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [queuedSend, setQueuedSend] = useState(null); // { message, context }
  const fileInputRef = useRef(null);
  const assetMenuRef = useRef(null);
  const profileBtnRef = useRef(null);
  const isControlledInput = typeof draftValue === 'string' && typeof onDraftChange === 'function';
  const input = isControlledInput ? draftValue : internalInput;
  const setInputValue = useCallback((value) => {
    if (isControlledInput) {
      onDraftChange(value);
      return;
    }
    setInternalInput(value);
  }, [isControlledInput, onDraftChange]);

  // Close the asset attach menu when clicking outside of it
  useEffect(() => {
    if (!assetMenuOpen) return;
    const handleClickOutside = (e) => {
      if (assetMenuRef.current && !assetMenuRef.current.contains(e.target)) {
        setAssetMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [assetMenuOpen]);

  const pinnedAssets = Array.isArray(currentChat?.pinned_assets) ? currentChat.pinned_assets : [];
  const MAX_PINNED_ASSETS = 5;
  const {
    assetError,
    canAddMoreAssets,
    pendingPins,
    pinnedAssetIds,
    setAssetError,
    setPendingPins,
    setPinnedAssetIds,
    unpinAsset,
    uploadAndPin,
  } = useChatAssetPins({
    chatId,
    pinnedAssets,
    updatePinnedAssets,
    maxPinnedAssets: MAX_PINNED_ASSETS,
  });

  const openAssetMenu = () => {
    if (!canAddMoreAssets) return;
    setAssetMenuOpen(true);
  };

  const toggleAssetMenu = () => {
    if (!canAddMoreAssets) return;
    setAssetMenuOpen((v) => !v);
  };

  const handleUnpinAsset = async (assetId) => {
    await unpinAsset(assetId);
  };

  const queuedUserMessageId = useMemo(() => `queued-user-${chatId || "unknown"}`, [chatId]);
  const queuedAssistantMessageId = useMemo(() => `queued-assistant-${chatId || "unknown"}`, [chatId]);
  const preflightWarningMessageId = useMemo(() => `preflight-warning-${chatId || "unknown"}`, [chatId]);

  const upgradePath = useMemo(() => {
    if (!username) return null;
    return `/settings/${username}/subscription`;
  }, [username]);
  
  // If user attempted to send while assets were processing, send automatically once assets are ready.
  useEffect(() => {
    if (!queuedSend) return;

    // If upload/extraction failed, cancel the queued send and restore the text.
    if (assetError) {
      removeEphemeralMessage(queuedUserMessageId);
      removeEphemeralMessage(queuedAssistantMessageId);
      setInputValue(queuedSend.message || "");
      setQueuedSend(null);
      return;
    }

    // Wait until there are no pending pins and we're not currently sending.
    if (pendingPins.length > 0 || sending) return;

    // Assets are ready: remove local status messages and send to backend.
    removeEphemeralMessage(queuedUserMessageId);
    removeEphemeralMessage(queuedAssistantMessageId);

    const { message, context } = queuedSend;
    setQueuedSend(null);
    if (message && chatId) {
      void sendMessage(chatId, message, context || {});
    }
  }, [
    assetError,
    chatId,
    pendingPins.length,
    queuedAssistantMessageId,
    queuedSend,
    queuedUserMessageId,
    removeEphemeralMessage,
    sendMessage,
    sending,
    setInputValue,
  ]);
  
  const handleSend = async () => {
    if (!input.trim() || sending || queuedSend) return;
    
    const messageToSend = input;
    const { shouldBlock, urls } = detectLinkScrapeRequest(messageToSend);
    if (shouldBlock) {
      const warningText = buildLinkScrapeBlockedMessage(urls);
      setPreflightWarning(warningText);
      setQuotaWarning(null);
      setSendError(null);
      // Optionally show it inline as an assistant bubble (AIChatPanel supports this).
      if (enableEphemeralWarnings) {
        addEphemeralMessage({
          id: preflightWarningMessageId,
          role: 'assistant',
          content: warningText,
          timestamp: Date.now(),
        });
      }
      return;
    }

    // Build selection context without using 'combination' category.
    // Always include a page_id when we know the currently viewed page.
    const resolvedPageId =
      selectedElement?.type === "website_page"
        ? selectedElement?.id
        : selectedElement?.website_page || pageId || null;

    const context = selectedElement ? {
      element_id: selectedElement.id,
      element_type: selectedElement.type,
      // allow backend to determine appropriate level/category when 'auto'
      level: 'auto',
      category: 'content',
      page_id: resolvedPageId,
      // scope will be refined in AIContext.sendMessage
      scope: 'auto'
    } : (resolvedPageId ? { page_id: resolvedPageId } : {});
    
    // Clear input immediately so the message doesn't linger in the textarea.
      setInputValue('');
    setQuotaWarning(null);
    setSendError(null);
    setPreflightWarning(null);

    // If assets are still uploading/extracting/pinning, queue the message until they're ready.
    if (pendingPins.length > 0) {
      setQueuedSend({ message: messageToSend, context });

      const now = Date.now();
      addEphemeralMessage({
        id: queuedUserMessageId,
        role: 'user',
        content: messageToSend,
        timestamp: now,
        pending: true,
      });
      addEphemeralMessage({
        id: queuedAssistantMessageId,
        role: 'assistant',
        content: 'Finishing to analyse files…',
        timestamp: now + 1,
      });
      return;
    }

    const result = await sendMessage(chatId, messageToSend, context);

    if (result?.error?.code === 'ai_messages_limit_reached') {
      // Restore the input so the user can retry after upgrading.
      setInputValue(messageToSend);
      setQuotaWarning({
        message: "You've reached your monthly AI message limit. Upgrade to get more messages.",
        resetDate: result?.error?.reset_date || null,
      });
      return;
    }

    if (result?.error) {
      // Restore the input so the user can retry.
      setInputValue(messageToSend);
      setSendError({
        message: result.error.message || "Network error. Please try again.",
      });
      return;
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  return (
    <div className={`relative p-3 border-t ${borders.default} ${surfaces.primary}`}>
      {isReviewMode ? (
        <div
          data-testid="ai-input-review-overlay"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-green-400/20"
        />
      ) : null}
      <div className="relative z-10">
      {/* Pinned assets bar + attach menu */}
      <div className="mb-2" data-tutorial-id="ai-chat-assets">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {pinnedAssets.map((a) => (
                <div
                  key={`pinned-${a.id}`}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full border ${borders.default} ${surfaces.tertiary}`}
                  title={a.original_filename || `Asset #${a.id}`}
                >
                  <CheckCircleIcon className="w-3.5 h-3.5 text-green-400" />
                  <span className={`${typography.caption} ${textColors.primary} max-w-[180px] truncate`}>
                    {a.original_filename || `Asset #${a.id}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleUnpinAsset(a.id)}
                    className={`p-0.5 rounded ${textColors.muted} ${textColors.hover} ${surfaces.hover}`}
                    aria-label="Unpin asset"
                    title="Unpin"
                  >
                    <CloseIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {pendingPins.map((p) => (
                <div
                  key={p.key}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full border ${borders.default} ${surfaces.tertiary} opacity-80`}
                  title={`${p.stage}: ${p.filename}`}
                >
                  <GearIcon className="w-3.5 h-3.5 animate-spin text-yellow-400" />
                  <span className={`${typography.caption} ${textColors.secondary} max-w-[180px] truncate`}>
                    {p.filename} ({p.stage})
                  </span>
                </div>
              ))}

              {pinnedAssets.length === 0 && pendingPins.length === 0 && (
                <button
                  type="button"
                  onClick={openAssetMenu}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${borders.default} ${surfaces.tertiary} ${surfaces.hover} transition-colors w-full min-w-0`}
                  aria-label="Choose files or images to use in this chat"
                  title="Choose files or images to use in this chat"
                >
                  <span className={`${typography.bodySmall} ${textColors.primary} flex-1 min-w-0 truncate whitespace-nowrap`}>
                    Click here to use files/images in this chat
                  </span>
                  <ArrowRightIcon className={`w-4 h-4 ${textColors.secondary} flex-shrink-0`} />
                </button>
              )}
            </div>
          </div>

          <div className="relative flex-shrink-0" ref={assetMenuRef}>
            <button
              type="button"
              onClick={toggleAssetMenu}
              disabled={!canAddMoreAssets}
              className={`p-2 rounded-md border ${borders.default} ${surfaces.secondary} ${surfaces.hover} transition-colors ${
                !canAddMoreAssets ? "opacity-50 cursor-not-allowed" : ""
              } ${textColors.primary}`}
              aria-label="Attach assets"
              title={!canAddMoreAssets ? "Maximum pinned assets reached" : "Attach assets"}
            >
              <ClipboardIcon className={`w-4 h-4 ${textColors.primary}`} />
            </button>

            {assetMenuOpen && (
              <div className={`absolute right-0 mt-2 w-44 rounded-lg shadow-xl border ${borders.default} ${surfaces.elevated} overflow-hidden z-10`}>
                <button
                  type="button"
                  onClick={() => {
                    setAssetMenuOpen(false);
                    fileInputRef.current?.click?.();
                  }}
                  className={`w-full text-left px-3 py-2 ${typography.bodySmall} ${textColors.primary} ${surfaces.hover}`}
                  disabled={!canAddMoreAssets}
                >
                  Upload new
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAssetMenuOpen(false);
                    setLibraryOpen(true);
                  }}
                  className={`w-full text-left px-3 py-2 ${typography.bodySmall} ${textColors.primary} ${surfaces.hover}`}
                  disabled={!canAddMoreAssets}
                >
                  Choose from uploaded
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                // allow picking the same file again
                const file = (e.target.files || [])[0] || null;
                e.target.value = "";
                if (!file) return;
                void uploadAndPin(file);
              }}
            />
          </div>

          {/* Website Profile button + dropdown */}
          <div className="relative flex-shrink-0" ref={profileBtnRef}>
            <button
              type="button"
              onClick={() => setProfileOpen((v) => !v)}
              className={`p-2 rounded-md border ${borders.default} ${surfaces.secondary} ${surfaces.hover} transition-colors ${textColors.primary}`}
              aria-label="Website profile"
              title="Website profile – customise how the AI writes for this site"
            >
              <TargetIcon className={`w-4 h-4 ${textColors.primary}`} />
            </button>

            <WebsiteProfileDropdown
              websiteId={websiteId}
              open={profileOpen}
              onClose={() => setProfileOpen(false)}
              anchorRef={profileBtnRef}
            />
          </div>
        </div>

        {assetError && (
          <div className={`mt-2 px-3 py-2 rounded-lg border ${borders.default} bg-red-50 dark:bg-red-950/30`}>
            <div className="flex items-start justify-between gap-3">
              <div className={`${typography.caption} ${textColors.secondary}`}>{assetError.message}</div>
              <button
                type="button"
                className={`${typography.caption} ${textColors.muted} hover:underline`}
                onClick={() => setAssetError(null)}
                aria-label="Dismiss asset error"
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      {sendError && (
        <div className={`mb-2 px-3 py-2 rounded-lg border ${borders.default} bg-red-50 dark:bg-red-950/30`}>
          <div className="flex items-start justify-between gap-3">
            <div className={`${typography.caption} ${textColors.secondary}`}>
              {sendError.message}
            </div>
            <button
              type="button"
              className={`${typography.caption} ${textColors.muted} hover:underline`}
              onClick={() => setSendError(null)}
              aria-label="Dismiss error"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {preflightWarning && !enableEphemeralWarnings && (
        <div className={`mb-2 px-3 py-2 rounded-lg border ${borders.default} bg-blue-50 dark:bg-blue-950/30`}>
          <div className="flex items-start justify-between gap-3">
            <div className={`${typography.caption} ${textColors.secondary}`} style={{ whiteSpace: 'pre-wrap' }}>
              {preflightWarning}
            </div>
            <button
              type="button"
              className={`${typography.caption} ${textColors.muted} hover:underline`}
              onClick={() => setPreflightWarning(null)}
              aria-label="Dismiss notice"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <textarea
          value={input}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (preflightWarning) setPreflightWarning(null);
          }}
          onFocus={() => onTextareaFocus?.()}
          onBlur={(e) => onTextareaBlur?.(e)}
          onKeyPress={handleKeyPress}
          placeholder={chatType === 'site_review' ? 'Ask AI to review your full website or answer its questions...' : 'Ask AI to modify your website...'}
          disabled={sending || !!queuedSend}
          ref={textareaRef}
          className={`flex-1 px-3 py-2 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 text-sm border ${borders.default} ${surfaces.primary} text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-dark-400 ${
            sending || queuedSend ? "opacity-70 cursor-not-allowed" : ""
          }`}
          rows={2}
        />
        <PrimaryButton
          onClick={handleSend}
          disabled={!input.trim() || sending || !!queuedSend}
          className="flex items-center justify-center px-2 py-2"
          aria-label={sending ? 'Sending message...' : 'Send message'}
          title={sending ? 'Sending message...' : 'Send message'}
        >
          {sending ? (
            <GearIcon className="w-4 h-4 animate-spin" />
          ) : (
            <SendIcon className="w-4 h-4" />
          )}
        </PrimaryButton>
      </div>
      {queuedSend && (
        <div className={`mt-2 ${typography.caption} ${textColors.muted}`}>
          Queued — finishing to analyse pinned files before sending…
        </div>
      )}
      {quotaWarning && (
        <div className={`mt-2 px-3 py-2 rounded-lg border ${borders.default} bg-yellow-50 dark:bg-yellow-950/30`}>
          <div className="flex items-start justify-between gap-3">
            <div className={`${typography.caption} ${textColors.secondary}`}>
              {quotaWarning.message}
            </div>
            <button
              type="button"
              className={`${typography.caption} ${textColors.muted} hover:underline`}
              onClick={() => setQuotaWarning(null)}
              aria-label="Dismiss warning"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
          <div className="mt-2 flex flex-col gap-2">
            <PrimaryButton
              type="button"
              onClick={() => {
                if (upgradePath) navigate(upgradePath);
              }}
              disabled={!upgradePath}
              className="w-full px-3 py-2 justify-center"
            >
              Upgrade for more messages
            </PrimaryButton>
            <span className={`${typography.caption} ${textColors.muted}`}>
              Resets next month.
            </span>
          </div>
        </div>
      )}
      {selectedElement && chatType !== 'site_review' && (
        <div className={`mt-2 px-3 py-2 rounded-lg border ${borders.default} ${surfaces.tertiary}`}>
          <p className={`${typography.caption} ${textColors.secondary}`}>
            Selected: {selectedElement.type} #{selectedElement.id}
          </p>
        </div>
      )}

      <AIAssetLibraryModal
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        disabledAssetIds={pinnedAssetIds}
        maxSelectable={MAX_PINNED_ASSETS}
        onSelect={async (asset) => {
          if (!asset?.id) return;
          if (!canAddMoreAssets) return;
          const key = `pending-lib-${asset.id}`;
          setAssetError(null);
          setPendingPins((prev) => [...prev, { key, filename: asset.original_filename || `Asset #${asset.id}`, stage: "pinning", assetId: asset.id }]);
          try {
            const ok = await setPinnedAssetIds([...pinnedAssetIds, asset.id]);
            if (ok) setLibraryOpen(false);
          } finally {
            setPendingPins((prev) => prev.filter((p) => p.key !== key));
          }
        }}
      />
      </div>
    </div>
  );
};

export default AIInputBox;
